import React, { useEffect, useState, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDebouncedValue } from '@mantine/hooks';
import {
  Button,
  RadioGroup,
  Radio,
  Group,
  Text,
  Textarea,
  Box,
  Title,
  Divider,
  TextInput,
  Select,
  Avatar,
  ActionIcon,
  Checkbox,
  CheckboxGroup,
  SimpleGrid,
  Stack,
  Code,
  Center,
  Loader,
  Alert,
  MantineTheme,
  useMantineTheme,
} from '@mantine/core';
import {
  User,
  Briefcase,
  Phone,
  Trash,
  Upload,
  FileUpload,
  Photo,
  X,
  AlertCircle,
  Icon as TablerIcon,
} from 'tabler-icons-react';
import { DatePicker } from '@mantine/dates';
import { useForm, formList } from '@mantine/form';
import { Dropzone, DropzoneStatus, MIME_TYPES } from '@mantine/dropzone';
import externalApi from '../api';
import endpoints from '../api/endpoints';
import { FormList } from '@mantine/form/lib/form-list/form-list';
import { showNotification } from '@mantine/notifications';
import { GraphUser, UDFPickListResults, TicketResponse, NetworkDrive } from '../types';

type AllGraphUsers = {
  '@odata.context': string;
  '@odata.nextLink': string;
  value: GraphUser[];
};

type SystemAccessTicket = {
  accessRequestType: string;
  userType: string;
  rehire: boolean;
  firstName: string;
  lastName: string;
  designatedSite: string;
  jobTitle: string;
  designation: string;
  extension: string;
  program: string;
  department: string;
  newExtension: string;
  existingStaffMember: GraphUser | undefined;
  newJobTitle: string;
  formerStaffName: string;
  effectiveDate: string;
  option: string;
  applicationAccess: string[];
  epicAccess: string;
  previousEpicAccount: boolean;
  clinicUser: string;
  cogitoReporting: boolean;
  ccoConfirmation: string;
  otherAccess: string;
  accessToRemove: string;
  networkDriveFolders: FormList<NetworkDrive>;
  emailDistributionGroups: string;
  comments: string;
};

interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
  image: string;
  label: string;
  value: string;
  description: string;
  initials: string;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ image, label, description, initials, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Group noWrap>
        <Avatar src={image}>{initials}</Avatar>
        <div>
          <Text size="sm">{label}</Text>
          <Text size="xs" color="dimmed">
            {description}
          </Text>
        </div>
      </Group>
    </div>
  )
);

const NewAccessTicket = () => {
  // const theme = useMantineTheme();
  const navigate = useNavigate();
  const [user, setUser] = useState<GraphUser>();
  const [manager, setManager] = useState<GraphUser>();
  const [onBehalfOf, setOnBehalfOf] = useState(false);
  const [departments, setDepartments] = useState<string[]>([]);
  const [programs, setPrograms] = useState<string[]>([]);
  const [designatedSites, setDesignatedSites] = useState<string[]>([]);
  const [userSearchInput, setUserSearchInput] = useState('');
  const [debouncedUserSearchInput] = useDebouncedValue(userSearchInput, 200);
  const [userSelectData, setUserSelectData] = useState<ItemProps[]>([]);
  const [existingUser, setExistingUser] = useState<GraphUser>();
  // const [attachments, setAttachments] = useState<File[]>([]);

  const [loadingState, setLoadingState] = useState(false);
  const [isDataLoaded, setDataLoaded] = useState(false);

  // const formData = new FormData();

  // const dropzoneChildren = (status: DropzoneStatus, theme: MantineTheme) => (
  //   <Group position="center" spacing="xl" style={{ minHeight: 220, pointerEvents: 'none' }}>
  //     <ImageUploadIcon status={status} style={{ color: getIconColor(status, theme) }} size={50} />

  //     <div>
  //       <Text size="xl" inline>
  //         Drag attachments here or click to select files
  //       </Text>
  //       <Text size="sm" color="dimmed" inline mt={7}>
  //         Attach as many files as you like, each file should not exceed 10mb
  //       </Text>
  //     </div>
  //   </Group>
  // );

  // const uploadFiles = (files: File[]) => {
  //   files.map((f) => formData.append('files', f, f.name));
  //   setAttachments((old) => [...old, ...files]);
  // };

  // const attachmentsList = attachments.map((f) => (
  //   <Group key={f.name}>
  //     <Text color="gray">{f.name}</Text>
  //     <Group>
  //       <ActionIcon variant="default" sx={{ color: theme.colors.red[7] }} onClick={() => formData.delete(f.name)}>
  //         <Trash size={16} />
  //       </ActionIcon>
  //     </Group>
  //   </Group>
  // ));

  const alphabet = ['F', 'G', 'I', 'J', 'K', 'M', 'P', 'R', 'T', 'U', 'V', 'X', 'Z'];

  const ticket: SystemAccessTicket = {
    accessRequestType: '',
    userType: '',
    rehire: false,
    firstName: '',
    lastName: '',
    designatedSite: '',
    jobTitle: '',
    designation: '',
    extension: '',
    program: '',
    department: '',
    newExtension: '',
    existingStaffMember: existingUser,
    newJobTitle: '',
    formerStaffName: '',
    effectiveDate: '',
    option: '',
    applicationAccess: [],
    epicAccess: '',
    previousEpicAccount: false,
    clinicUser: '',
    cogitoReporting: false,
    ccoConfirmation: 'false',
    otherAccess: '',
    accessToRemove: '',
    networkDriveFolders: formList([{ driveLetter: '', folder: '', access: '' }]),
    emailDistributionGroups: '',
    comments: '',
  };

  const form = useForm<SystemAccessTicket>({
    initialValues: ticket,
    validate: {},
  });

  const networkDriveFields = form.values.networkDriveFolders.map((_, index) => (
    <Group key={index}>
      <Select
        size="sm"
        {...form.getListInputProps('networkDriveFolders', index, 'driveLetter')}
        label="Drive Letter"
        placeholder="Select Drive Letter"
        nothingFound="No Program Found"
        data={alphabet}
      />
      <TextInput label="Network Folder" {...form.getListInputProps('networkDriveFolders', index, 'folder')} />
      <RadioGroup
        label="Access"
        color="blue"
        size="sm"
        {...form.getListInputProps('networkDriveFolders', index, 'access')}
      >
        <Radio value="Read" label="Read" />
        <Radio value="Modify" label="Modify" />
      </RadioGroup>
      <ActionIcon color="red" variant="hover" onClick={() => form.removeListItem('networkDriveFolders', index)}>
        <Trash size={16} />
      </ActionIcon>
    </Group>
  ));

  // function ImageUploadIcon({ status, ...props }: React.ComponentProps<TablerIcon> & { status: DropzoneStatus }) {
  //   if (status.accepted) {
  //     return <Upload {...props} />;
  //   }

  //   if (status.rejected) {
  //     return <X {...props} />;
  //   }

  //   return <FileUpload {...props} />;
  // }

  // function getIconColor(status: DropzoneStatus, theme: MantineTheme) {
  //   return status.accepted
  //     ? theme.colors[theme.primaryColor][theme.colorScheme === 'dark' ? 4 : 6]
  //     : status.rejected
  //     ? theme.colors.red[theme.colorScheme === 'dark' ? 4 : 6]
  //     : theme.colorScheme === 'dark'
  //     ? theme.colors.dark[0]
  //     : theme.colors.gray[7];
  // }

  async function submitForm() {
    let response!: TicketResponse;
    setLoadingState(true);
      try {
          if (onBehalfOf) {
              response = await externalApi
                  .post(endpoints.submitSystemAccessTicket.url, {
                      json: { ...form.values, requester: manager, submittedBy: user },
                      timeout: 30000,
                  })
                  .json();
          } else {
              response = await externalApi
                  .post(endpoints.submitSystemAccessTicket.url, { json: { ...form.values, requester: user }, timeout: 30000 })
                  .json();
          }
      } catch (e) {
          showNotification({
              title: 'Error Submitting Form',
              color: 'red',
              message: String(e),
          });
          setLoadingState(false);
      }
    

    if (response.success === true) {
      // externalApi.post(`${endpoints.uploadFiles.url}${response.ticketId}`, { body: formData });
      navigate(`/request-submitted/${response.ticketId}`);
    } else {
      console.log(response);
      setLoadingState(false);
      showNotification({
        title: 'Error Submitting Form',
        color: 'red',
        message: response.response,
      });
    }
  }

  async function loadUser() {
    const me: GraphUser = await externalApi.get(endpoints.graphMe.url).json();
    setUser(me);
  }

  // WARNING - This is VERY innefficent right now. It will make about 31 API calls in a row to get all users.
  // async function loadAllUsers() {
  //     let users: AllGraphUsers = await externalApi.get(endpoints.graphUsers.url).json();

  //     while (users !== undefined && users?.['@odata.nextLink'] !== undefined)
  //     {
  //         let nextUsers: AllGraphUsers = await externalApi.get(users?.['@odata.nextLink']).json();
  //         users.value = users.value.concat(nextUsers.value);
  //         users['@odata.nextLink'] = nextUsers['@odata.nextLink'];
  //         console.log(users);
  //     }

  //     setAllUsers(users);
  // }

  // async function getProfilePhoto(userId: string): Promise<string> {
  //   try {
  //     let response = await externalApi.get(`${endpoints.graphProfilePhoto.url}/${userId}/photo/$value`, {
  //       headers: { 'Content-Type': 'image/jpeg' },
  //     });

  //     if (response.ok) {
  //       let photoBlob = await response.blob();
  //       if (photoBlob !== null) {
  //         window.URL = window.URL || window.webkitURL;
  //         return window.URL.createObjectURL(photoBlob);
  //       }
  //     }
  //   } catch {
  //     //Quick and dirty failure mode to just return a blank photo if all else fails.
  //   }

  //   //An empty string will create a generic empty profile photo
  //   return '';
  // }

  async function getUsersFromGraph(displayNameSearch: string) {
    const users: AllGraphUsers = await externalApi
      .get(`${endpoints.graphUsers.url}&$search="displayName:${displayNameSearch}"`, {
        headers: { ConsistencyLevel: 'eventual' },
      })
      .json();

    let searchUsers: ItemProps[] = [];
    users.value.forEach(async (searchUser) => {
      if (searchUser.id !== null) {
        let profilePhotoUrl: string = ''; //await getProfilePhoto(searchUser.id);
        let initials: string =
          searchUser.givenName?.length > 0 ? `${searchUser.givenName?.at(0)}${searchUser.surname?.at(0)}` : '';

        searchUsers.push({
          image: profilePhotoUrl,
          description: searchUser.jobTitle,
          value: searchUser.id,
          label: searchUser.displayName,
          initials: initials,
        });
      }
    });

    setUserSelectData(searchUsers);
  }

  async function loadHRData() {
    const programNames: string[] = await externalApi.get(endpoints.getProgramNames.url).json();
    const departmentNames: string[] = await externalApi.get(endpoints.getDepartmentNames.url).json();

    setPrograms(programNames);
    setDepartments(departmentNames);
  }

  async function loadDesignatedSites() {
    const sites: UDFPickListResults = await externalApi.get(endpoints.getDesignatedSites.url).json();
    setDesignatedSites(sites.values);
  }

  async function loadExistingUser(userPrincipal: string) {
    const graphUser: GraphUser = await externalApi
      .get(
        `${endpoints.graphProfilePhoto.url}/${userPrincipal}?$select=businessPhones,displayName,givenName,jobTitle,mail,mobilePhone,officeLocation,preferredLanguage,surname,department,userPrincipalName,id`
      )
      .json();

    setExistingUser(graphUser);
    form.setFieldValue('existingStaffMember', graphUser);
  }

  async function loadManager(userPrincipal: string) {
    const graphManager: GraphUser = await externalApi
      .get(
        `${endpoints.graphProfilePhoto.url}/${userPrincipal}?$select=businessPhones,displayName,givenName,jobTitle,mail,mobilePhone,officeLocation,preferredLanguage,surname,department,userPrincipalName,id`
      )
      .json();

    setManager(graphManager);
  }

  async function loadAllData() {
    loadUser();
    loadHRData();
    loadDesignatedSites();
    setDataLoaded(true);
  }

  useEffect(() => {
    loadAllData();
    if (debouncedUserSearchInput !== '') getUsersFromGraph(debouncedUserSearchInput);
  }, [debouncedUserSearchInput]);

  return (
    <>
          <Alert icon={<AlertCircle size={40} />} title="Access Requests must be submitted by a Director, Department Manager or Preceptor. EA's and Admin Assts may submit on behalf of: Manager/Director." color="red" radius="lg">
              
          </Alert>
      <Title my="sm" order={1}>
        IT System Access
      </Title>
      <form onSubmit={form.onSubmit(() => submitForm())}>
        <Box>
          <Checkbox
            color="blue"
            label="Submitting On Behalf of Manager"
            checked={onBehalfOf}
            onChange={(event) => setOnBehalfOf(event.currentTarget.checked)}
          />

          {!onBehalfOf && (
            <Box>
              <Divider my="sm" label="Requester" labelProps={{ size: 'lg' }} />

              <Stack>
                <Group>
                  <User />
                  <Text>{user?.displayName}</Text>
                </Group>
                <Group>
                  <Briefcase />
                  <Text>{user?.jobTitle},</Text>
                  <Text>{user?.department}</Text>
                </Group>
                <Group>
                  <Phone />
                  <Text>{user?.businessPhones}</Text>
                </Group>
              </Stack>
            </Box>
          )}
          {onBehalfOf && (
            <Box>
              <Divider my="sm" label="Manager" labelProps={{ size: 'lg' }} />
              <Stack>
                <Select
                  searchable
                  label="Select Manager"
                  nothingFound="No Manager Found"
                  itemComponent={SelectItem}
                  onSearchChange={(input: string) => setUserSearchInput(input)}
                  onChange={(value: string) => loadManager(value)}
                  data={userSelectData}
                />
                <Group>
                  <User />
                  <Text>{manager?.displayName}</Text>
                </Group>
                <Group>
                  <Briefcase />
                  <Text>{manager?.jobTitle},</Text>
                  <Text>{manager?.department}</Text>
                </Group>
                <Group>
                  <Phone />
                  <Text>{manager?.businessPhones}</Text>
                </Group>
                <Text>Submitted by: {user?.displayName}</Text>
              </Stack>
            </Box>
          )}
        </Box>

        <Divider my="lg" label="User Information" labelProps={{ size: 'lg' }} />
        <Select
          label="User Type"
          my="md"
          {...form.getInputProps('userType')}
          placeholder="Select a User Type"
          required
          data={[
            { value: 'Staff', label: 'Staff' },
            { value: 'Physician', label: 'Physician' },
            { value: 'Student', label: 'Student' },
            { value: 'Vendor', label: 'Vendor' },
            { value: 'Other', label: 'Other' },
          ]}
        />

        <RadioGroup
          {...form.getInputProps('accessRequestType')}
          defaultValue="NewUser"
          label="Access Request Type"
          color="blue"
          size="sm"
        >
          <Radio value="NewUser" label="New User" />
          <Radio value="ExistingUser" label="Existing User Access Change" />
          <Radio value="Transfer" label="Transfer" />
          <Radio value="Separation" label="Separation" />
          <Radio value="LOA" label="Leave of Absence" />
        </RadioGroup>

        {form.values['accessRequestType'] === 'NewUser' && (
          <Box>
            <Group my="md">
              <TextInput {...form.getInputProps('firstName')} required label="First Name" />
              <TextInput {...form.getInputProps('lastName')} required label="Last Name" />
              <TextInput {...form.getInputProps('extension')} label="Phone Ext." size="sm" />
            </Group>
            <Group>
              <Select
                my="md"
                size="sm"
                {...form.getInputProps('designatedSite')}
                required
                label="Designated Site"
                placeholder="Select Designated Site"
                nothingFound="No Site Found"
                data={designatedSites}
              />
              <TextInput {...form.getInputProps('jobTitle')} required label="Job Title" />
              <TextInput {...form.getInputProps('designation')} label="Professional Designation" />
            </Group>
            <Checkbox my="sm" size="md" color="blue" label="Rehire?" {...form.getInputProps('rehire')} />
          </Box>
        )}

        {(form.values['accessRequestType'] === 'ExistingUser' || form.values['accessRequestType'] === 'Transfer') && (
          <Box>
            <Group my="md">
              <Select
                required
                searchable
                label="Select User"
                nothingFound="No Existing Staff Member Found"
                itemComponent={SelectItem}
                onSearchChange={(input: string) => setUserSearchInput(input)}
                onChange={(value: string) => loadExistingUser(value)}
                data={userSelectData}
              />
              <TextInput {...form.getInputProps('newExtension')} required label="New Ext" />
            </Group>
          </Box>
        )}

        {(form.values['accessRequestType'] === 'Separation' || form.values['accessRequestType'] === 'LOA') && (
          <Box>
            <Group my="md">
              <Select
                searchable
                label="Select User"
                nothingFound="No Existing Staff Member Found"
                itemComponent={SelectItem}
                onSearchChange={(input: string) => setUserSearchInput(input)}
                onChange={(value: string) => loadExistingUser(value)}
                data={userSelectData}
              />
            </Group>
          </Box>
        )}

        {(form.values['accessRequestType'] === 'Separation' || form.values['accessRequestType'] === 'LOA') && (
          <Box>
            <TextInput
              my="md"
              {...form.getInputProps('formerStaffName')}
              label="User Full Name (if user not in pick list):"
              size="sm"
            />
            <TextInput {...form.getInputProps('jobTitle')} label="User Job Title (if user not in pick list)" />
          </Box>
        )}

        {form.values['accessRequestType'] === 'Transfer' && (
          <Box my="md">
            <TextInput {...form.getInputProps('newJobTitle')} required label="New Job Title:" size="sm" />
          </Box>
        )}

        {!isDataLoaded && (
          <Center>
            <Loader variant="dots" my="xl" size="lg" />
          </Center>
        )}

        {(form.values['accessRequestType'] === 'Transfer' || form.values['accessRequestType'] === 'NewUser') &&
          isDataLoaded &&
          form.values['userType'] === 'Physician' && (
            <Box>
              <Select
                my="md"
                size="sm"
                {...form.getInputProps('program')}
                searchable
                label="New Program"
                placeholder="Select Program"
                nothingFound="No Program Found"
                data={programs}
              />
              <Select
                my="md"
                size="sm"
                {...form.getInputProps('department')}
                searchable
                label="New Department"
                placeholder="Select Department"
                nothingFound="No Department Found"
                data={departments}
              />
            </Box>
          )}

        {(form.values['accessRequestType'] === 'Transfer' || form.values['accessRequestType'] === 'NewUser') &&
          isDataLoaded &&
          form.values['userType'] !== 'Physician' && (
            <Box>
              <Select
                my="md"
                size="sm"
                {...form.getInputProps('program')}
                searchable
                required
                label="New Program"
                placeholder="Select Program"
                nothingFound="No Program Found"
                data={programs}
              />
              <Select
                my="md"
                size="sm"
                {...form.getInputProps('department')}
                searchable
                required
                label="New Department"
                placeholder="Select Department"
                nothingFound="No Department Found"
                data={departments}
              />
            </Box>
          )}
        <Box>
          <Divider my="lg" label="Access Details" labelProps={{ size: 'lg' }} />
          <DatePicker
            {...form.getInputProps('effectiveDate')}
            required
            label="Effective Date"
            firstDayOfWeek="sunday"
          />
          {(form.values['accessRequestType'] === 'Transfer' ||
            form.values['accessRequestType'] === 'ExistingUser' ||
            form.values['accessRequestType'] === 'NewUser') && (
            <Box>
              <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: 600, cols: 1, spacing: 'sm' }]}>
                <CheckboxGroup
                  my="md"
                  {...form.getInputProps('applicationAccess')}
                  label="Required Application Access"
                  description="Computer Login(Active Directory) and Payroll Access will be set up automatically."
                >
                  <Checkbox value="CCIS" label="CCIS" />
                  <Checkbox value="Centralink" label="Centralink" />
                  <Checkbox value="ConnectingOntario" label="ConnectingOntario" />
                  <Checkbox value="eHealth - OneID" label="eHealth - OneID" />
                  <Checkbox value="Email/OWA" label="Email/OWA" />
                  <Checkbox value="EMPI" label="EMPI" />
                  <Checkbox value="eNLB" label="eNLB" />
                  <Checkbox value="Epic" label="Epic" />
                  <Checkbox value="OLIS" label="OLIS" />
                  <Checkbox value="ORRS" label="ORRS" />
                  <Checkbox value="Point Click Care (PCC)" label="Point Click Care (PCC)" />
                  <Checkbox value="PTAC" label="PTAC" />
                  <Checkbox value="Surge Learning" label="Surge Learning" />
                  <Checkbox value="Tap N Go" label="Tap N Go" />
                  <Checkbox value="Other" label="Other" />
                </CheckboxGroup>
              </SimpleGrid>
            </Box>
          )}
          {form.values['applicationAccess'].includes('ConnectingOntario') && (
            <Box
              sx={(theme) => ({
                backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
                maxWidth: '50%',
                padding: theme.spacing.xl,
                borderRadius: theme.radius.md,
                '@media (max-width: 755px)': {
                  fontSize: 14,
                  maxWidth: '100%',
                },
              })}
            >
              <RadioGroup
                required
                label="Has the user completed the mandatory cGTA Privacy and Security Overview module in uLEARN?"
                {...form.getInputProps('ccoConfirmation')}
              >
                <Radio value="true" label="Yes" />
                <Radio value="false" label="No" />
              </RadioGroup>
            </Box>
          )}
          {form.values['applicationAccess'].includes('Other') &&
            (form.values['accessRequestType'] === 'Transfer' ||
              form.values['accessRequestType'] === 'ExistingUser' ||
              form.values['accessRequestType'] === 'NewUser') && (
              <TextInput label="Other Access" {...form.getInputProps('otherAccess')} />
            )}
          {(form.values['accessRequestType'] === 'Transfer' || form.values['accessRequestType'] === 'ExistingUser') && (
            <TextInput label="Access to Remove" {...form.getInputProps('accessToRemove')} />
          )}

          {(form.values['accessRequestType'] === 'Transfer' ||
            form.values['accessRequestType'] === 'ExistingUser' ||
            form.values['accessRequestType'] === 'NewUser') && (
            <Box>
              {networkDriveFields}
              <Button
                my="sm"
                onClick={() => form.addListItem('networkDriveFolders', { driveLetter: '', folder: '', access: '' })}
              >
                Add Network Drive
              </Button>

              <Textarea
                {...form.getInputProps('emailDistributionGroups')}
                label="Email Distribution Groups"
                placeholder="List all that apply, please type the exact group name"
              />
            </Box>
          )}
          {form.values['applicationAccess'].includes('Epic') &&
            (form.values['accessRequestType'] === 'Transfer' ||
              form.values['accessRequestType'] === 'ExistingUser' ||
              form.values['accessRequestType'] === 'NewUser') && (
              <Box>
                <Divider my="md" label="Epic Access Details" labelProps={{ size: 'lg' }} />
                <TextInput
                  label="List Epic Template Names or Indicate 'Copy Same As Person'"
                  {...form.getInputProps('epicAccess')}
                />
                <TextInput
                  label="If this is a clinic user, does User need to be added to a Schedule Template? If so, which clinic?"
                  {...form.getInputProps('clinicUser')}
                />
                <Checkbox
                  my="sm"
                  color="blue"
                  label="Does User need Cogito Reporting?"
                  {...form.getInputProps('cogitoReporting')}
                />
                <Checkbox
                  my="sm"
                  color="blue"
                  label="Does User have a previous CEHC Epic Account?"
                  {...form.getInputProps('previousEpicAccount')}
                />
              </Box>
            )}
          <Textarea {...form.getInputProps('comments')} label="Comments" />
          {/* <Dropzone
            my="sm"
            onDrop={(files) => uploadFiles(files)}
            onReject={(files) => console.log('rejected files', files)}
            style={{ maxWidth: '50%' }}
            maxSize={3 * 1024 ** 4}
            accept={[MIME_TYPES.xls, MIME_TYPES.xlsx, MIME_TYPES.doc, MIME_TYPES.docx, MIME_TYPES.pdf]}
          >
            {(status) => dropzoneChildren(status, theme)}
          </Dropzone> */}
        </Box>
        {/* {attachmentsList} */}
        {/*<Code block>{JSON.stringify(form.values, null, 2)}</Code> */}
        {form.values['accessRequestType'] !== '' && (
          <Button my="sm" type="submit" loading={loadingState}>
            Submit Ticket
          </Button>
        )}
      </form>
    </>
  );
};

export default NewAccessTicket;
